import React, { useState, useEffect, useCallback } from 'react';
// import { useNavigate } from 'react-router-dom'; // Removed unused import
import { useAuth } from '../utils/firebase';
import { collection, query, orderBy, limit, getDocs, addDoc, serverTimestamp, getDoc, doc } from 'firebase/firestore';
import { db } from '../utils/firebase';
// import Logo from '../components/Logo'; // Removed unused import
import TeamNavbar from '../components/TeamNavbar';

const TeamFeed = () => {
  // const navigate = useNavigate(); // Removed unused variable
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newPost, setNewPost] = useState('');
  const [team, setTeam] = useState(null);

  const loadTeamData = useCallback(async () => {
    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const userData = userDoc.data();
      
      if (userData?.teamId) {
        const teamDoc = await getDoc(doc(db, 'teams', userData.teamId));
        if (teamDoc.exists()) {
          setTeam(teamDoc.data());
        }
      }
    } catch (error) {
      console.error('Error loading team data:', error);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      loadTeamData();
      loadFeed();
    }
  }, [user, loadTeamData]);

  const loadFeed = async () => {
    try {
      const postsQuery = query(
        collection(db, 'teamPosts'),
        orderBy('timestamp', 'desc'),
        limit(20)
      );
      const postsSnapshot = await getDocs(postsQuery);
      const postsData = postsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPosts(postsData);
    } catch (error) {
      console.error('Error loading feed:', error);
    } finally {
      setLoading(false);
    }
  };

  const createPost = async () => {
    if (!newPost.trim() || !team) return;

    try {
      await addDoc(collection(db, 'teamPosts'), {
        teamId: team.id,
        teamName: team.name,
        content: newPost,
        timestamp: serverTimestamp(),
        likes: 0,
        comments: []
      });
      setNewPost('');
      loadFeed(); // Refresh feed
    } catch (error) {
      console.error('Error creating post:', error);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Just now';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading feed...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Team Navigation Bar */}
      <TeamNavbar />

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          {/* Create Post */}
          {team && (
            <div className="bg-gray-800 rounded-xl p-6 mb-6 border border-gray-700">
              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-blue-500 rounded-full flex items-center justify-center text-white font-bold">
                  {team.name?.charAt(0) || 'T'}
                </div>
                <div className="flex-1">
                  <textarea
                    value={newPost}
                    onChange={(e) => setNewPost(e.target.value)}
                    placeholder="What's happening with your team?"
                    className="w-full bg-gray-700 text-white rounded-lg p-3 border border-gray-600 focus:border-green-500 focus:outline-none resize-none"
                    rows="3"
                  />
                  <div className="flex justify-between items-center mt-3">
                    <div className="text-sm text-gray-400">
                      Share your achievements, game results, or team updates!
                    </div>
                    <button
                      onClick={createPost}
                      disabled={!newPost.trim()}
                      className="bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white px-4 py-2 rounded-lg font-semibold transition duration-200"
                    >
                      Post
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Feed Posts */}
          <div className="space-y-6">
            {posts.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">📱</div>
                <h3 className="text-xl font-bold text-white mb-2">No Posts Yet</h3>
                <p className="text-gray-400">Be the first to share something with the trivia community!</p>
              </div>
            ) : (
              posts.map((post) => (
                <div key={post.id} className="bg-gray-800 rounded-xl p-6 border border-gray-700">
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-blue-500 rounded-full flex items-center justify-center text-white font-bold">
                      {post.teamName?.charAt(0) || 'T'}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <h3 className="font-bold text-white">{post.teamName}</h3>
                        <span className="text-gray-400 text-sm">•</span>
                        <span className="text-gray-400 text-sm">{formatTimestamp(post.timestamp)}</span>
                      </div>
                      <p className="text-gray-300 mb-4">{post.content}</p>
                      
                      {/* Post Actions */}
                      <div className="flex items-center space-x-6">
                        <button className="flex items-center space-x-2 text-gray-400 hover:text-green-500 transition-colors">
                          <span>👍</span>
                          <span>{post.likes || 0}</span>
                        </button>
                        <button className="flex items-center space-x-2 text-gray-400 hover:text-blue-500 transition-colors">
                          <span>💬</span>
                          <span>{post.comments?.length || 0}</span>
                        </button>
                        <button className="flex items-center space-x-2 text-gray-400 hover:text-purple-500 transition-colors">
                          <span>🔄</span>
                          <span>Share</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamFeed; 